// API service layer for backend communication
// Handles Firebase Auth token management and API requests

import { getLocalImpersonationHeaders } from '../dev/localImpersonation';
import { getAuth } from '../firebase/config';
import type { Car, Customer, Invitation, Organization, OrganizationSettings, Rental, ServiceLog, User } from '../models';
import { getApiUrl } from './apiConfig';

const API_URL = getApiUrl();
console.log('[API] Final API URL:', API_URL);

interface ApiError {
  error: string;
  message?: string;
}

interface InvitationPayload {
  email: string;
  role: Invitation['role'];
  expiresAt?: string;
}

/**
 * Gets the current user's Firebase ID token
 * Forces token refresh to ensure it's valid
 * Adds a small delay after refresh to avoid clock skew issues
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.warn('getAuthToken: No current user');
      return null;
    }
    // Force refresh to get a fresh token
    const token = await user.getIdToken(true);
    // Small delay to avoid "Token used too early" clock skew errors
    // This gives the token time to become valid
    await new Promise(resolve => setTimeout(resolve, 100));
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Makes an authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const auth = getAuth();
  const localImpersonationHeaders = getLocalImpersonationHeaders(auth.currentUser?.email);
  const token = await getAuthToken();

  if (!token && !localImpersonationHeaders) {
    console.error('API Request failed: No auth token available');
    throw new Error('User not authenticated');
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...localImpersonationHeaders,
        ...options.headers,
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`API Network/CORS error for ${endpoint}:`, message);
    throw new Error(`Network/CORS error: ${message}`);
  }

  if (!response.ok) {
    // Log the error for debugging
    if (response.status === 401) {
      const errorData: ApiError = await response.json().catch(() => ({ error: 'Authentication failed' }));
      const errorMsg = errorData.error || errorData.message || 'Authentication failed';
      
      // Check for clock skew errors
      if (errorMsg.includes('too early') || errorMsg.includes('clock')) {
        console.warn(`API clock skew detected for ${endpoint}, token may need more time to become valid`);
      } else {
        console.error(`API Authentication failed for ${endpoint}:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorMsg,
        });
      }
      
      throw new Error(errorMsg);
    }
    
    const error: ApiError = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `API request failed: ${response.statusText}`);
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();
    return data as T;
  }
  
  return {} as T;
}

// Cars API
export const carsApi = {
  getAll: async (): Promise<Car[]> => {
    return apiRequest<Car[]>('/api/cars');
  },

  getById: async (id: string): Promise<Car> => {
    return apiRequest<Car>(`/api/cars/${id}`);
  },

  create: async (carData: Omit<Car, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>): Promise<Car> => {
    return apiRequest<Car>('/api/cars', {
      method: 'POST',
      body: JSON.stringify(carData),
    });
  },

  update: async (id: string, carData: Partial<Car>): Promise<Car> => {
    return apiRequest<Car>(`/api/cars/${id}`, {
      method: 'PUT',
      body: JSON.stringify(carData),
    });
  },

  delete: async (id: string): Promise<void> => {
    return apiRequest<void>(`/api/cars/${id}`, {
      method: 'DELETE',
    });
  },
};

// Customers API
export const customersApi = {
  getAll: async (): Promise<Customer[]> => {
    return apiRequest<Customer[]>('/api/customers');
  },

  getById: async (id: string): Promise<Customer> => {
    return apiRequest<Customer>(`/api/customers/${id}`);
  },

  create: async (customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>): Promise<Customer> => {
    return apiRequest<Customer>('/api/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  },

  update: async (id: string, customerData: Partial<Customer>): Promise<Customer> => {
    return apiRequest<Customer>(`/api/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  },

  delete: async (id: string): Promise<void> => {
    return apiRequest<void>(`/api/customers/${id}`, {
      method: 'DELETE',
    });
  },
};

// Rentals API
export const rentalsApi = {
  getAll: async (): Promise<Rental[]> => {
    return apiRequest<Rental[]>('/api/rentals');
  },

  getById: async (id: string): Promise<Rental> => {
    return apiRequest<Rental>(`/api/rentals/${id}`);
  },

  create: async (rentalData: Omit<Rental, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>): Promise<Rental> => {
    return apiRequest<Rental>('/api/rentals', {
      method: 'POST',
      body: JSON.stringify(rentalData),
    });
  },

  update: async (id: string, rentalData: Partial<Rental>): Promise<Rental> => {
    return apiRequest<Rental>(`/api/rentals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(rentalData),
    });
  },

  delete: async (id: string): Promise<void> => {
    return apiRequest<void>(`/api/rentals/${id}`, {
      method: 'DELETE',
    });
  },
};

// Services API
export const servicesApi = {
  getAll: async (): Promise<ServiceLog[]> => {
    return apiRequest<ServiceLog[]>('/api/services');
  },

  getById: async (id: string): Promise<ServiceLog> => {
    return apiRequest<ServiceLog>(`/api/services/${id}`);
  },

  create: async (serviceData: Omit<ServiceLog, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>): Promise<ServiceLog> => {
    return apiRequest<ServiceLog>('/api/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  },

  update: async (id: string, serviceData: Partial<ServiceLog>): Promise<ServiceLog> => {
    return apiRequest<ServiceLog>(`/api/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  },

  delete: async (id: string): Promise<void> => {
    return apiRequest<void>(`/api/services/${id}`, {
      method: 'DELETE',
    });
  },
};

// Health check
export const healthApi = {
  check: async (): Promise<{ status: string }> => {
    return fetch(`${API_URL}/health`).then(res => res.json());
  },
};

export const devToolsApi = {
  getUsersForOrganization: async (organizationId: string): Promise<User[]> => {
    return fetch(`${API_URL}/api/dev/organizations/${organizationId}/users`, {
      headers: {
        'X-Local-Dev-Tools': '1',
      },
    }).then(async (response) => {
      if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({ error: 'Unable to load organization users' }));
        throw new Error(error.error || error.message || 'Unable to load organization users');
      }

      const data = await response.json();
      return data.users as User[];
    });
  },
};

export const settingsApi = {
  getCurrentOrganization: async (): Promise<Organization> => {
    return apiRequest<Organization>('/api/organization/current');
  },

  updateCurrentOrganization: async (payload: { name: string; settings: OrganizationSettings }): Promise<Organization> => {
    return apiRequest<Organization>('/api/organization/current', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  updateProfile: async (payload: { displayName: string }): Promise<User> => {
    return apiRequest<User>('/api/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  getStaff: async (): Promise<User[]> => {
    return apiRequest<User[]>('/api/staff');
  },

  updateStaffRole: async (userId: string, role: User['role']): Promise<User> => {
    return apiRequest<User>(`/api/staff/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  },

  removeStaff: async (userId: string): Promise<void> => {
    return apiRequest<void>(`/api/staff/${userId}`, {
      method: 'DELETE',
    });
  },

  getInvitations: async (): Promise<Invitation[]> => {
    return apiRequest<Invitation[]>('/api/invitations');
  },

  createInvitation: async (payload: InvitationPayload): Promise<Invitation> => {
    return apiRequest<Invitation>('/api/invitations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  cancelInvitation: async (invitationId: string): Promise<void> => {
    return apiRequest<void>(`/api/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  },

  resendInvitation: async (invitationId: string, expiresAt?: string): Promise<Invitation> => {
    return apiRequest<Invitation>(`/api/invitations/${invitationId}/resend`, {
      method: 'POST',
      body: JSON.stringify({ expiresAt }),
    });
  },
};
